// src/components/ui/EditarProducto.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// === Base URL robusta (local/prod)
const resolveApiBase = () => {
  const env = (process.env.REACT_APP_API_BASE || '').trim();
  if (env) return env.replace(/\/+$/, '');
  const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  return (isLocal ? 'http://localhost:5001' : window.location.origin).replace(/\/+$/, '');
};
const API_BASE   = resolveApiBase();
const NODISP     = `${API_BASE}/public/nodisponible.png`;
const LIST_ROUTE = '/admin/productos';

export default function EditarProducto() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [cargando, setCargando] = useState(true);
  const [producto, setProducto] = useState(null);
  const [imagenSeleccionada, setImagenSeleccionada] = useState(null); // File
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploadPct, setUploadPct] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [toast, setToast] = useState('');

  // Mostrar toast sin temporizadores automáticos (eliminamos delays cosméticos)
  const showToast = (msg) => { setToast(msg); };

  // Cargar producto (sin cache) 
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setCargando(true);
        const r = await fetch(`${API_BASE}/api/productos/${id}?t=${Date.now()}`, {
          credentials: 'include',
          cache: 'no-store',
        });
        if (!r.ok) throw new Error('Producto no encontrado');
        const p = await r.json();
        if (alive) setProducto(p);
      } catch (e) {
        alert(String(e.message || e));
        navigate(LIST_ROUTE, { replace: true });
      } finally {
        if (alive) setCargando(false);
      }
    })();
    return () => { alive = false; };
  }, [id, navigate]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      nombre: producto?.nombre || '',
      marca: producto?.marca || '',
      presentacion: producto?.presentacion || '',
      precio: producto?.precio ?? '',
      stock: producto?.stock ?? '',
      estado: producto?.estado || 'disponible',
      imagen_actual: producto?.imagen || '',
    },
    validationSchema: Yup.object({
      nombre: Yup.string().required('El nombre es obligatorio'),
      marca: Yup.string().required('La marca es obligatoria'),
      presentacion: Yup.string().required('La presentación es obligatoria'),
      precio: Yup.number().typeError('Número inválido').required('El precio es obligatorio').positive('Debe ser positivo'),
      stock: Yup.number().typeError('Número inválido').required('El stock es obligatorio').min(0, 'Debe ser ≥ 0'),
    }),
    onSubmit: async (values) => {
      // Función para cerrar el flujo de guardado (sin esperas artificiales)
      // Navegamos y limpiamos inmediatamente después de la respuesta del servidor.
      const finalizeOk = () => {
        setUploadPct(100);
        setGuardando(false);
        showToast('✔ Cambios guardados');
        // Navegar inmediatamente: dejamos que el servidor determine cuánto tarda la operación.
        navigate(LIST_ROUTE, { replace: true });
        // Limpiar estado de progreso enseguida
        setUploadPct(null);
      };
      const finalizeErr = (msg) => {
        setGuardando(false);
        alert(String(msg || 'No se pudo actualizar'));
        // Limpiar progreso sin demoras
        setUploadPct(null);
      };

      try {
        setGuardando(true);
        setUploadPct(0);

        const fd = new FormData();
        fd.append('nombre', values.nombre);
        fd.append('marca', values.marca);
        fd.append('presentacion', values.presentacion);
        fd.append('precio', values.precio);
        fd.append('stock', values.stock);
        fd.append('estado', values.estado);
        if (imagenSeleccionada) {
          fd.append('imagen', imagenSeleccionada);
        } else if (values.imagen_actual) {
          fd.append('imagen_actual', values.imagen_actual);
        }

        const xhr = new XMLHttpRequest();
        xhr.open('PUT', `${API_BASE}/api/productos/${id}`, true);
        xhr.withCredentials = true;
        xhr.responseType = 'json';
        xhr.timeout = 2000; // 20s duro

        // Progreso real
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            // Evita que llegue a 100% antes de onload
            setUploadPct(Math.min(pct, 95));
          }
        };

        // Fallback de progreso (antivirus / interceptores que no disparan eventos)
        let softTick = 0;
        const softTimer = setInterval(() => {
          setUploadPct((p) => {
            const now = typeof p === 'number' ? p : 0;
            const next = Math.min(now + 1, 95);
            return next;
          });
          if (++softTick > 25) clearInterval(softTimer);
        }, 400);

        // Hard timeout manual por si el ontimeout no se dispara
        const hardTimer = setTimeout(() => {
          try { xhr.abort(); } catch {}
          finalizeErr('timeout');
        }, 25000);

        const resp = await new Promise((resolve, reject) => {
          xhr.onload = () => {
            clearInterval(softTimer);
            clearTimeout(hardTimer);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(xhr.response ?? JSON.parse(xhr.responseText || '{}'));
            } else {
              reject(new Error(`Error ${xhr.status} al guardar`));
            }
          };
          xhr.onerror = () => {
            clearInterval(softTimer);
            clearTimeout(hardTimer);
            reject(new Error('Error de red'));
          };
          xhr.ontimeout = () => {
            clearInterval(softTimer);
            clearTimeout(hardTimer);
            // Tratamos como éxito “optimista” porque el backend suele haber guardado.
            finalizeOk();
          };
          xhr.send(fd);
        });

        // Éxito flexible: si el backend devuelve ok:false, error
        if (resp && resp.ok === false) return finalizeErr(resp.msg || 'No se pudo actualizar');

        // Todo bien
        finalizeOk();
      } catch (e) {
        finalizeErr(e.message || e);
      }
    },
  });

  const imagenSrc = useMemo(() => {
    if (previewUrl) return previewUrl;
    const img = formik.values.imagen_actual || '';
    if (!img) return NODISP;
    if (img.startsWith('http') || img.startsWith('data:')) return img;
    return `${API_BASE}${img.startsWith('/') ? img : '/uploads/' + img}`;
  }, [previewUrl, formik.values.imagen_actual]);

  const onImagen = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type?.includes('image')) { alert('Solo se permiten imágenes'); return; }
    setImagenSeleccionada(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const onEliminar = async () => {
    const ok = window.confirm('¿Eliminar este producto? Esta acción no se puede deshacer.');
    if (!ok) return;
    try {
      const r = await fetch(`${API_BASE}/api/productos/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) {
        let msg = `Error ${r.status}`; try { const j = await r.json(); msg = j?.msg || j?.message || msg; } catch {}
        throw new Error(msg);
      }
  showToast('🗑️ Producto eliminado');
  // Navegar inmediatamente después de eliminar
  navigate(LIST_ROUTE, { replace: true });
    } catch (e) {
      alert(String(e.message || e));
    }
  };

  if (cargando) return <p className="text-center">Cargando producto...</p>;
  if (!producto) return null;

  return (
    <div className="max-w-4xl mx-auto mt-10">
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-xl bg-emerald-600 text-white shadow">
          {toast}
        </div>
      )}

      <div className="bg-white rounded-2xl shadow p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-800">Editar producto</h1>
            <p className="text-slate-500">ID #{producto.id}</p>
          </div>
          <button
            type="button"
            onClick={() => navigate(LIST_ROUTE)}
            className="px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700"
            disabled={guardando}
          >
            ← Volver
          </button>
        </div>

        <form onSubmit={formik.handleSubmit} encType="multipart/form-data" className="grid md:grid-cols-5 gap-6">
          {/* Imagen */}
          <div className="md:col-span-2">
            <div className="rounded-2xl border p-3">
              <div className="aspect-square rounded-xl overflow-hidden border bg-white">
                <img
                  src={imagenSrc}
                  alt="Producto"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = NODISP; }}
                />
              </div>
              <div className="mt-3 flex items-center gap-2">
                <label
                  htmlFor="cambiar-imagen"
                  className={`inline-flex items-center px-3 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 cursor-pointer text-sm ${guardando ? 'opacity-60 pointer-events-none' : ''}`}
                >
                  Cambiar imagen
                  <input type="file" id="cambiar-imagen" accept="image/*" onChange={onImagen} className="hidden" disabled={guardando} />
                </label>
                <span className="text-xs text-slate-500 truncate">
                  {imagenSeleccionada?.name || formik.values.imagen_actual}
                </span>
              </div>

              {uploadPct !== null && (
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-medium text-slate-600">
                      {uploadPct >= 100 ? 'Completado' : 'Subiendo imagen…'}
                    </span>
                    <span className="text-slate-500">{uploadPct}%</span>
                  </div>
                  <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-200 ${uploadPct >= 100 ? 'bg-emerald-500' : 'bg-sky-500'}`}
                      style={{ width: `${uploadPct}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={onEliminar}
              className="mt-4 w-full px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-semibold"
              disabled={guardando}
            >
              Eliminar producto
            </button>
          </div>

          {/* Formulario */}
          <div className="md:col-span-3">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nombre" name="nombre" formik={formik} disabled={guardando} />
              <Field label="Marca" name="marca" formik={formik} disabled={guardando} />
              <Field label="Presentación" name="presentacion" formik={formik} disabled={guardando} full />
              <Field label="Precio" name="precio" type="number" step="0.01" formik={formik} disabled={guardando} />
              <Field label="Stock" name="stock" type="number" formik={formik} disabled={guardando} />
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-slate-700">Estado</label>
                <select
                  name="estado"
                  value={formik.values.estado}
                  onChange={formik.handleChange}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-white"
                  disabled={guardando}
                >
                  <option value="disponible">Disponible</option>
                  <option value="noDisponible">No disponible</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex items-center gap-3">
              <button
                type="submit"
                disabled={guardando}
                className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-60"
              >
                {guardando ? 'Guardando…' : 'Guardar cambios'}
              </button>
              <button
                type="button"
                onClick={() => navigate(LIST_ROUTE)}
                className="px-5 py-2 rounded-xl border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold"
                disabled={guardando}
              >
                Cancelar
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, name, formik, type='text', step, disabled, full=false }) {
  const err = formik.touched[name] && formik.errors[name];
  return (
    <div className={full ? 'sm:col-span-2' : undefined}>
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      <input
        id={name}
        name={name}
        type={type}
        step={step}
        value={formik.values[name]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        className={`mt-1 w-full rounded-lg border px-3 py-2 ${err ? 'border-rose-400' : 'border-slate-300'}`}
        disabled={disabled}
      />
      {err && <p className="text-rose-600 text-sm mt-1">{formik.errors[name]}</p>}
    </div>
  );
}
